import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, renameFeature } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { ApiException, FeatureModel } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import axios from 'axios';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { Messages } from '../../../../utils/message-constants';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
interface RenameFeatureDialogProps {
  open: boolean;
  selectedFeature: FeatureModel;
  setSelectedFeature: Dispatch<SetStateAction<FeatureModel | null>>;
  changeLogRoles: string[];
  handleClose: () => void;
}

export default function RenameFeatureDialog({
  open,
  handleClose,
  selectedFeature,
  setSelectedFeature,
  changeLogRoles,
}: RenameFeatureDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [featureName, setFeatureName] = useState(selectedFeature.name);
  const [hasError, setHasError] = useState(false);
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      renameFeature(selectedFeature.name, featureName, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Renamed('Feature'),
      });

      queryClient.setQueryData<FeatureModel[] | undefined>(
        [UserKeys.Features],
        (oldData) => {
          if (oldData) {
            return oldData.map((feature) =>
              feature.etags === selectedFeature.etags
                ? { ...feature, name: featureName }
                : feature
            );
          }
        }
      );

      setHasError(false);
      setSelectedFeature(null);
      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message?.includes('already exists')) {
          setHasError(true);
        } else {
          handleErrors({
            error,
            snackbarMessage: Messages.Errors.Network.Renaming('this feature'),
          });
        }
      }
    },
    onSettled: () => {
      createEvent(analytics.calendars.resource_calendars.rename_feature, {
        module: ModuleList.modules.calendars.name,
        section: ModuleList.modules.calendars.sections.resourceCalendars,
      });
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, CalendarRoles.EditFeatures.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setHasError(false);
        setSelectedFeature(null);
        handleClose();
      }}
      title="Rename Feature"
      dialogContent={
        <Stack>
          <TextField
            autoFocus
            id="feature"
            error={hasError}
            helperText={hasError ? 'Feature name is taken.' : ' '}
            label="Name"
            fullWidth
            autoComplete="off"
            value={featureName}
            onChange={(event) => setFeatureName(event.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit,
        disabled: featureName === '',
        title: 'Rename Feature',
      }}
    />
  );
}
